import React, { useContext } from "react";

import { logout, redirect, resetBackLink, saveBackLink } from "../actions";
import { hasAuthCookies } from "../libs/tokens";
import withRedux from "../withRedux";
import { Profile, ICustomWindow, ProfileFlags, Connected } from "../types";
import { TokenService } from "../libs/tokenService";
import DisplayModeContext, { DisplayMode } from "../contexts/displayModeContext";
import { Redirect, Route, RouteChildrenProps } from "react-router";
import queryString from "query-string";

export function getFlagRedirect(flags: ProfileFlags, path: string): string | undefined {
	const allFlags = Object.values(flags).reduce((previous, on) => {
		return previous && on;
	}, 1);

	if (allFlags && isInFlagPage(path)) {
		return undefined;
	}
	if (flags.mustAcceptTOC === 1 && path !== "/tos-review") {
		return "/tos-review";
	} else if (flags.mustAcceptTOC === 1 && path === "/tos-review") {
		return undefined;
	}

	if (flags.mustAlterPassword === 1 && path !== "/alter-password") {
		return "/alter-password";
	} else if (flags.mustAlterPassword === 1 && path === "/alter-password") {
		return undefined;
	}

	if (flags.showOnboarding === 1 && path !== "/onboarding") {
		return "/onboarding";
	} else if (flags.showOnboarding === 1 && path === "/onboarding") {
		return undefined;
	}

	return undefined;
}

function isInFlagPage(path: string): boolean {
	const pathnames = ["/tos-review", "/alter-password", "/onboarding"];
	return pathnames.includes(path);
}
interface Props extends RouteChildrenProps {
	logout: typeof logout;
	redirect: typeof redirect;
	authenticated: boolean;
	loggingOut: boolean;
	profile: Profile;
	tokenExpires: number;
	tokenService: TokenService;
	saveBackLink: Connected<typeof saveBackLink>;
	resetBackLink: Connected<typeof resetBackLink>;
	backlink: Location;
}

export default function (ComposedComponent: React.ComponentType<any>) {
	const Authentication = ({ authenticated, backlink, loggingOut, ...props }: Props) => {
		const displayMode = useContext(DisplayModeContext);

		if (loggingOut && process.env.REACT_APP_AFTER_LOGOUT_REDIRECT_URI) {
			window.location.href = process.env.REACT_APP_AFTER_LOGOUT_REDIRECT_URI;
			return null;
		}

		if (!hasAuthCookies()) {
			const backLink = props.location;
			props.saveBackLink(backLink);
			if (process.env.REACT_APP_AFTER_LOGOUT_REDIRECT_URI) {
				window.location.href = process.env.REACT_APP_AFTER_LOGOUT_REDIRECT_URI;
				return;
			}
		}

		if (props.tokenService.shouldRefresh() || !authenticated) {
			props.tokenService.refresh();
		}

		if (props.profile.flags && displayMode === DisplayMode.STANDALONE) {
			const pathname = getFlagRedirect(props.profile.flags, props.match.path);

			if (pathname) {
				return (
					<Redirect
						to={{
							pathname,
							state: {},
						}}
					/>
				);
			}
		}

		if (!authenticated) {
			const backLink = props.location;
			props.saveBackLink(backLink);
			if (process.env.REACT_APP_AFTER_LOGOUT_REDIRECT_URI) {
				window.location.href = process.env.REACT_APP_AFTER_LOGOUT_REDIRECT_URI;
				return;
			}
		}

		const { query } = queryString.parseUrl(props.location.search);
		let redirect = query["redirect"];

		if (!isInFlagPage(props.match.path) && !redirect && backlink && displayMode === DisplayMode.STANDALONE) {
			redirect = backlink.pathname;
			if (backlink.search) {
				redirect = `${redirect}?${backlink.search}`;
			}
			props.resetBackLink();
		}

		if (redirect) {
			return (
				<Redirect
					to={{
						//@ts-ignore
						pathname: redirect,
						state: {},
					}}
				/>
			);
		}

		return <Route {...props} render={() => <ComposedComponent {...props} />} />;
	};

	function mapStateToProps({ auth, profile }) {
		const myWindow: ICustomWindow = window;
		const tokenService = myWindow.tokenService;
		return { ...auth, profile, tokenService };
	}

	return withRedux({ logout, redirect, saveBackLink, resetBackLink }, mapStateToProps)(Authentication);
}
